import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionListRenderItemInfo } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { Modal } from '@/feature/alerts/components/Modal';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { BetslipModalHeader } from '@/feature/betslip-sbk/components/BetslipModalHeader';
import { betslipModalStyles } from '@/feature/betslip-sbk/components/shared-modal-styles';
import { useComboOdds, usePreviousComboOdds } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useColumnWidths } from '@/feature/betslip-sbk/hooks/use-column-widths';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { changedSelectionIdsSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import {
    BottomSheetFooter,
    BottomSheetFooterProps,
    BottomSheetModal,
    BottomSheetSectionList,
} from '@gorhom/bottom-sheet';
import { useShallow } from 'zustand/react/shallow';

import { BetSlipModalStickyHeaderRow } from '../BetSlipModalStickyHeaderRow';
import { OddsChangeFooter } from './OddsChangeFooter';
import { OddsChangeItemParlay } from './OddsChangeItemParlay';
import { OddsChangeItemSingle } from './OddsChangeItemSingle';

export type OddsChangeModalRef = {
    show: () => void;
    dismiss: () => void;
};

const MODAL_ID = 'oddsChangeModal';

type SectionData = {
    title: string;
    data: string[];
};

const COMBO_ITEM_ID = '__combo__';

const keyExtractor = (item: string) => item;

export const OddsChangeModal = React.forwardRef<OddsChangeModalRef, object>((_props, ref) => {
    const modalRef = useRef<BottomSheetModal>(null);
    const { t } = useTranslation('betslip_sbk');
    const { bottom } = useSafeAreaInsets();
    const isPredictions = useIsPredictionsProduct();

    const { columnWidths, createLayoutHandler } = useColumnWidths({
        oldOdds: 90,
        newOdds: 80,
    });

    const comboOdds = useComboOdds();
    const previousComboOdds = usePreviousComboOdds();
    const hasComboBets = useSbkBetSlipStore(state => Object.keys(state.selections).length > 1);
    const showComboRow = hasComboBets && comboOdds !== previousComboOdds && comboOdds !== 1;

    const changedSelectionIds = useSbkBetSlipStore(useShallow(changedSelectionIdsSelector));

    const { acceptAllOddsChanges } = useSbkBetSlipStore(state => state.actions);

    // Auto-dismiss when all odds changes are resolved
    const hasChanges = changedSelectionIds.length > 0 || showComboRow;
    useEffect(() => {
        if (!hasChanges) {
            modalRef.current?.dismiss();
        }
    }, [hasChanges]);

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current?.present();
        },
        dismiss: () => {
            modalRef.current?.dismiss();
        },
    }));

    const handleClose = useCallback(() => {
        modalRef.current?.dismiss();
    }, []);

    const handleAccept = useCallback(() => {
        GASbkEvents.reviewMultiplierChanges('accept_multipliers');
        acceptAllOddsChanges();
        modalRef.current?.dismiss();
    }, [acceptAllOddsChanges]);

    // Build section data: combo row (if applicable) + changed singles
    const sections: SectionData[] = useMemo(() => {
        const data: string[] = [];
        if (showComboRow) {
            data.push(COMBO_ITEM_ID);
        }
        data.push(...changedSelectionIds);
        return [{ title: 'odds_changes', data }];
    }, [showComboRow, changedSelectionIds]);

    const renderSectionHeader = useCallback(
        () => (
            <BetSlipModalStickyHeaderRow>
                <Box flex={1}>
                    <Text variant="bodySmall" color="neutral300">
                        {t('picks_header')}
                    </Text>
                </Box>
                <Box minWidth={columnWidths.oldOdds} pr="s16">
                    <Text variant="bodySmall" color="neutral300" textAlign="right">
                        {t(isPredictions ? 'old_price' : 'old_odds')}
                    </Text>
                </Box>
                <Box minWidth={columnWidths.newOdds}>
                    <Text variant="bodySmall" color="neutral300" textAlign="right">
                        {t(isPredictions ? 'new_price' : 'new_odds')}
                    </Text>
                </Box>
            </BetSlipModalStickyHeaderRow>
        ),
        [t, isPredictions, columnWidths.oldOdds, columnWidths.newOdds]
    );

    const renderItem = useCallback(
        ({ item: id }: SectionListRenderItemInfo<string>) => {
            if (id === COMBO_ITEM_ID) {
                return <OddsChangeItemParlay columnWidths={columnWidths} createLayoutHandler={createLayoutHandler} />;
            }
            return (
                <OddsChangeItemSingle
                    selectionId={id}
                    columnWidths={columnWidths}
                    createLayoutHandler={createLayoutHandler}
                />
            );
        },
        [columnWidths, createLayoutHandler]
    );

    const renderListHeader = useCallback(
        () => (
            <BetslipModalHeader
                title={t(isPredictions ? 'price_changes' : 'multiplier_changes')}
                description={t(isPredictions ? 'price_changes_description' : 'odds_changes_description')}
            />
        ),
        [t, isPredictions]
    );

    const footerStyle = useMemo(() => ({ paddingBottom: bottom || 16 }), [bottom]);

    const footerComponent = useCallback(
        (props: BottomSheetFooterProps) => (
            <BottomSheetFooter {...props} style={betslipModalStyles.contentContainer}>
                <Box style={footerStyle}>
                    <OddsChangeFooter onAccept={handleAccept} onClose={handleClose} />
                </Box>
            </BottomSheetFooter>
        ),
        [handleAccept, handleClose, footerStyle]
    );

    return (
        <Modal id={MODAL_ID} modalRef={modalRef} hasBottomSheetView={false} footerComponent={footerComponent}>
            <BottomSheetSectionList
                sections={sections}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                renderSectionHeader={renderSectionHeader}
                ListHeaderComponent={renderListHeader}
                stickySectionHeadersEnabled={true}
                contentContainerStyle={betslipModalStyles.listContent}
                showsVerticalScrollIndicator={showScrollIndicator}
                enableFooterMarginAdjustment={true}
            />
        </Modal>
    );
});
